import { useEffect, useRef } from "react";

import connectToSocket from "../../services/socket-io";

/**
 * Assina um evento do Socket.IO pelo tempo de vida do componente.
 *
 * Existe para o padrão que o resto do app repete à mão (abrir socket no
 * useEffect, registrar handler, desconectar no cleanup) não precisar ser
 * reescrito — e para o handler poder mudar a cada render sem derrubar e
 * reabrir a conexão, que é de onde vêm os listeners duplicados.
 *
 * @param {string} event  nome do evento (convenção `entidade:acao`)
 * @param {Function} handler  chamado com o payload do evento
 * @param {Array} deps  dependências extras que devem reabrir a conexão
 */
const useSocketSubscription = (event, handler, deps = []) => {
  const handlerRef = useRef(handler);

  // O handler mais recente fica numa ref: assim a conexão só é recriada
  // quando `event`/`deps` mudam, e nunca porque a página redefiniu a função.
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!event) return undefined;

    const socket = connectToSocket();
    const listener = (...args) => {
      if (typeof handlerRef.current === "function") {
        handlerRef.current(...args);
      }
    };

    socket.on(event, listener);

    return () => {
      socket.off(event, listener);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, ...deps]);
};

export default useSocketSubscription;
